"use client";

import { useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import type { CartMutationResult } from "@/lib/cart/cart-mutation-result";
import {
	CART_LINE_CHANGED_EVENT,
	CHECKOUT_DIAGNOSTIC_SCHEMA_VERSION,
	type CartLineChangeAction,
	type CartLineChangeErrorCode,
	type CartLineChangeSurface,
} from "@/lib/analytics/checkout-diagnostic-events";
import { consumeThrottled } from "@/checkout/lib/throttled-event";
import { deleteCartLine, updateCartLineQuantity } from "./actions";
import { useCart, type MutationExpectation } from "./cart-context";

type MutationError = MutationExpectation & { status: "rejected" | "unknown" };

/** What `cart_line_changed` knows about a change before it is written. */
type LineChangeContext = {
	surface: CartLineChangeSurface;
	action: CartLineChangeAction;
	linesCount: number | null;
};

/** One `cart_line_changed` per action and outcome in this window. */
const LINE_CHANGED_THROTTLE_MS = 10_000;
/**
 * Throttle key family of the rejected/unknown outcomes. The page budget counts per
 * family, so a busy stream of confirmed changes can never use up the budget of the
 * failures, which are the signal this event exists for.
 */
const LINE_CHANGED_FAILURE_THROTTLE_FAMILY = `${CART_LINE_CHANGED_EVENT}_failure`;

/** The requested target relative to the quantity on screen; `set` when that is unknown. */
function lineChangeAction(target: MutationExpectation, shown: number | undefined): CartLineChangeAction {
	if (target.quantity === null) return "remove";
	if (shown === undefined || target.quantity === shown) return "set";
	return target.quantity > shown ? "increment" : "decrement";
}

/**
 * `cart_line_changed` once the server answered: the outcome is the server's own answer,
 * `error_code` only says whether the action call itself threw (the Saleor code of a
 * refusal is in the server's `[cart] line change` line). Never throws.
 */
function captureLineChanged(
	checkoutId: string,
	context: LineChangeContext,
	outcome: CartMutationResult["status"],
	threw: boolean,
): void {
	try {
		const family = outcome === "confirmed" ? CART_LINE_CHANGED_EVENT : LINE_CHANGED_FAILURE_THROTTLE_FAMILY;
		const gate = consumeThrottled(`${family}:${context.action}:${outcome}`, LINE_CHANGED_THROTTLE_MS);
		if (!gate.emit) return;
		const errorCode: CartLineChangeErrorCode | null = threw ? "action_threw" : null;
		posthog.capture(CART_LINE_CHANGED_EVENT, {
			event_schema_version: CHECKOUT_DIAGNOSTIC_SCHEMA_VERSION,
			surface: context.surface,
			action: context.action,
			outcome,
			error_code: errorCode,
			lines_count: context.linesCount,
			checkout_id: checkoutId,
			suppressed_count: gate.suppressed,
		});
	} catch {
		// Analytics must never break a cart change.
	}
}

/** Quantity/delete writes share one handover to the authoritative rendered cart.
 * Unknown results keep their absolute target in the provider so switching surfaces
 * cannot lose recovery or unlock checkout. Status checks never replay a write.
 */
export function useCartMutation({
	checkoutId,
	remove,
	onDeleteConfirmed,
}: {
	checkoutId: string | null;
	remove?: (lineId: string) => Promise<CartMutationResult>;
	onDeleteConfirmed?: (lineId: string) => void;
}) {
	const cart = useCart();
	const router = useRouter();
	const [isPending, startTransition] = useTransition();
	const [rejected, setRejected] = useState<MutationError | null>(null);
	const inFlight = useRef(false);
	const error: MutationError | null = cart.unresolvedMutation
		? { ...cart.unresolvedMutation, status: "unknown" }
		: rejected;

	function run(target: MutationExpectation, retryUnknown = false) {
		if (!checkoutId || inFlight.current || (target.quantity !== null && target.quantity < 1)) return;
		if (retryUnknown) {
			if (!cart.retryUnknownMutation(target)) return;
		} else if (cart.isMutationBlocked() || !cart.acquireMutation()) return;
		inFlight.current = true;
		setRejected(null);
		// Analytics context, read BEFORE the write: afterwards the snapshot may already
		// show the new quantity. The drawer is a modal sheet, so an open drawer means the
		// change came from it; the only other caller is the cart page.
		const analytics: LineChangeContext = { surface: "cart_page", action: "set", linesCount: null };
		try {
			const snapshot = cart.readMutationSnapshot?.() ?? null;
			analytics.surface = cart.isOpen ? "drawer" : "cart_page";
			analytics.action = lineChangeAction(
				target,
				snapshot?.find((line) => line.id === target.lineId)?.quantity,
			);
			analytics.linesCount = snapshot ? snapshot.length : null;
		} catch {
			// Analytics only.
		}
		startTransition(async () => {
			let result: CartMutationResult;
			let threw = false;
			try {
				result =
					target.quantity === null
						? await (remove ? remove(target.lineId) : deleteCartLine(checkoutId, target.lineId))
						: await updateCartLineQuantity(checkoutId, target.lineId, target.quantity);
			} catch {
				threw = true;
				result = { status: "unknown" };
			}
			captureLineChanged(checkoutId, analytics, result.status, threw);
			if (result.status === "confirmed") {
				cart.awaitMutationSnapshot(target);
				if (target.quantity === null) onDeleteConfirmed?.(target.lineId);
			} else if (result.status === "rejected" && !retryUnknown) {
				cart.releaseMutation();
				setRejected({ ...target, status: "rejected" });
			} else {
				// A rejected retry does not establish that the earlier uncertain write failed.
				cart.retainUnknownMutation(target);
			}
			inFlight.current = false;
			router.refresh();
		});
	}

	return {
		mutate: (lineId: string, quantity: number | null) => run({ lineId, quantity }),
		retry: () => {
			if (error) run(error, error.status === "unknown");
		},
		checkStatus: () => {
			if (inFlight.current || !cart.unresolvedMutation) return;
			startTransition(() => router.refresh());
		},
		error,
		isPending,
	};
}
